// Modèle de coûts en crédits — module PUR, sans dépendance serveur.
// Importable côté client (UI) comme côté serveur. La logique serveur
// (checkCredits / deductCredits, accès Supabase) vit dans credits.ts.

export type ModuleType = 'transcription' | 'correction' | 'coding' | 'analysis' | 'note-tech'

// ── Plan Pro ────────────────────────────────────────────────────────────────
// Coûts inchangés (référence historique : 1300 crédits / souscription).

/** Coûts fixes Pro par opération (crédits entiers). */
export const FIXED_COSTS = {
  coding: 154,        // Module 03 — Codification (par session)
  analysis: 80,       // Module 04 — Rapport d'analyse (par rapport)
  'note-tech': 150,   // Module 01 — Note technique
} as const

/** Coûts variables Pro (crédits par minute). */
export const PER_MINUTE_COSTS = {
  transcription: 0.57, // Module 02 — Transcription (durée audio)
  correction: 0.59,    // Module 02 — Correction du verbatim (durée du verbatim)
} as const

// ── Plan gratuit ────────────────────────────────────────────────────────────
// Calibration distincte : 100 crédits offerts correspondent EXACTEMENT à une
// session sur UN seul des DEUX modules accessibles — transcription ou
// codification (décision du 2026-10-12 ; le rapport d'analyse a rejoint
// `PRO_ONLY_MODULES`). Pour la codification, le forfait laisse 10 crédits
// résiduels « inutilisables » : le verrou `free_module_used` empêche de les
// dépenser ailleurs.

export const FREE_PLAN = {
  /** Solde initial offert à l'inscription. */
  initialCredits: 100,
  /** Transcription : 100 crédits = 60 min ⇒ 100/60 crédit/min ≈ 1.667/min. */
  transcriptionPerMinute: 100 / 60,
  /** Codification : forfait fixe (≤ 5 entretiens), reste 10 inutilisables. */
  codingFlat: 90,
  /**
   * Rapport : forfait fixe du plan gratuit.
   *
   * ⚠️ PLUS ATTEIGNABLE depuis le 2026-10-12 : le rapport est réservé au Pro
   * (`PRO_ONLY_MODULES`), donc `checkCredits` bloque avant tout calcul de coût.
   * La constante est conservée pour que `computeCost(…, { isPro: false })` reste
   * total sur `ModuleType` — et sa valeur est de toute façon identique au tarif
   * Pro (`FIXED_COSTS.analysis`). À supprimer si le plan gratuit n'est plus
   * jamais censé évoquer ce module.
   */
  analysisFlat: 80,
  /** Plafond entretiens par session en plan gratuit (coding & analysis). */
  maxEntretiens: 5,
} as const

// ── Plan Pro — optimisation utilisateur ─────────────────────────────────────
// Le forfait Pro de coding/analysis est rentabilisé jusqu'à un certain nombre
// d'entretiens ; en-dessous on suggère discrètement à l'utilisateur d'ajouter
// plus d'entretiens pour le même coût (non bloquant).

export const PRO_OPTIMAL = {
  /** Codification : 154 crédits couvrent jusqu'à 15 entretiens. */
  codingMaxEntretiens: 15,
  /** Rapport : 80 crédits couvrent jusqu'à 20 entretiens. */
  analysisMaxEntretiens: 20,
  /** Seuil entretiens en-dessous duquel afficher le conseil d'optimisation. */
  suggestThreshold: 10, // 1-9 entretiens → tip affiché
} as const

// ── Plans globaux ───────────────────────────────────────────────────────────

export const PLAN_CREDITS = {
  free: FREE_PLAN.initialCredits,
  pro: 1300,
} as const

/**
 * Modules JAMAIS accessibles en plan gratuit.
 *
 * `analysis` (rapport d'analyse) y est entré le 2026-10-12, par décision
 * commerciale : l'offre gratuite se limite désormais à la TRANSCRIPTION et à la
 * CODIFICATION. Elle l'avait quitté auparavant (« option C »), d'où le besoin de
 * dater la règle — c'est la liste qui fait autorité, et elle est la source de
 * `checkCredits`, de `getFreePlanModuleStatus` et des textes publics.
 */
export const PRO_ONLY_MODULES: ModuleType[] = ['note-tech', 'correction', 'analysis']

// ── Compat legacy ───────────────────────────────────────────────────────────
// Anciennes constantes conservées pour les imports existants ailleurs dans le
// code. Préférer FREE_PLAN.* pour le code nouveau.

/** @deprecated Remplacé par FREE_PLAN.transcriptionPerMinute. */
export const FREE_TRANSCRIPTION_MAX_MINUTES = 60
/** @deprecated Remplacé par FREE_PLAN.maxEntretiens. */
export const FREE_CODIFICATION_MAX_ENTRETIENS = FREE_PLAN.maxEntretiens

// ── Calcul de coût ──────────────────────────────────────────────────────────

export interface CostOptions {
  /** Durée en minutes — modules à coût variable (transcription, correction). */
  minutes?: number
  /** Plan tarifaire à appliquer (par défaut Pro pour compat ascendante). */
  isPro?: boolean
}

/**
 * Coût exact d'une opération, en crédits (arrondi au centième).
 *
 * Plan Pro (par défaut) :
 *  - transcription / correction → minutes × tarif Pro
 *  - coding / analysis / note-tech → tarif fixe Pro
 *
 * Plan gratuit (isPro: false) :
 *  - transcription → minutes × FREE_PLAN.transcriptionPerMinute
 *  - coding → FREE_PLAN.codingFlat
 *  - analysis → FREE_PLAN.analysisFlat
 *  - correction / note-tech → 0 (le coût est moot, le module est bloqué amont)
 */
export function computeCost(moduleType: ModuleType, optsOrMinutes?: CostOptions | number): number {
  const opts: CostOptions =
    typeof optsOrMinutes === 'number'
      ? { minutes: optsOrMinutes }
      : (optsOrMinutes ?? {})
  const isPro = opts.isPro !== false // défaut Pro pour compat
  const minutes = Math.max(0, opts.minutes ?? 0)

  if (isPro) {
    if (moduleType === 'transcription' || moduleType === 'correction') {
      const rate = PER_MINUTE_COSTS[moduleType]
      return Math.round(minutes * rate * 100) / 100
    }
    return FIXED_COSTS[moduleType]
  }

  // Plan gratuit
  if (moduleType === 'transcription') {
    return Math.round(minutes * FREE_PLAN.transcriptionPerMinute * 100) / 100
  }
  if (moduleType === 'coding') return FREE_PLAN.codingFlat
  if (moduleType === 'analysis') return FREE_PLAN.analysisFlat
  // correction / note-tech : pas de tarif gratuit (toujours bloqués amont)
  return 0
}

/**
 * Nouveau solde après une souscription / recharge Pro.
 * free → pro : le solde est porté à 1300 (les crédits gratuits sont cosmétiques).
 * pro → recharge : 1300 crédits ajoutés au solde existant.
 */
export function proTopUpCredits(currentCredits: number, wasPro: boolean): number {
  const base = wasPro ? (Number(currentCredits) || 0) : 0
  return base + PLAN_CREDITS.pro
}

/** Durée d'une période d'abonnement Pro. */
export const PRO_PERIOD_DAYS = 30

/**
 * Nouvelle échéance après une souscription / recharge Pro.
 *
 * La période s'AJOUTE à celle en cours : re-souscrire au 10ᵉ jour donne 50 jours
 * restants, pas 30. Un `greatest(…, now)` traite le cas d'une échéance déjà
 * dépassée mais pas encore rétrogradée (le job ne passe qu'une fois par jour) :
 * sans lui, l'utilisateur paierait pour du temps déjà écoulé.
 *
 * ⚠️ DUPLICATION ASSUMÉE, même motif que `proTopUpCredits` : la règle vit aussi
 * dans `activate_subscription_tx` (migration 000013), qui est le chemin VIVANT.
 * Cette version ne sert qu'aux chemins Moneroo legacy, conservés comme rollback
 * (D-006) et voués à disparaître en Phase 8. Elle existe pour qu'un rollback ne
 * recrée PAS le défaut que 000013 corrige — un Pro sans échéance, donc immortel.
 */
export function proExpiryAfterTopUp(currentExpiry: string | null, now: Date = new Date()): string {
  const current = currentExpiry ? new Date(currentExpiry).getTime() : 0
  const base = Number.isFinite(current) && current > now.getTime() ? current : now.getTime()
  return new Date(base + PRO_PERIOD_DAYS * 24 * 60 * 60 * 1000).toISOString()
}

/** Préavis VISÉ avant l'échéance. Le client est prévenu au plus tôt à J-3. */
export const EXPIRY_WARNING_LEAD_DAYS = 3

/**
 * Fenêtre des échéances à faire signaler par l'avertissement d'expiration.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI ELLE COMMENCE À `now` ET NON À `now + 2 jours`
 *
 * La version précédente visait `[now+2j, now+3j]` — une fenêtre de 24 h.
 * Combinée à un planificateur quotidien, elle était contiguë d'un passage à
 * l'autre, donc correcte en régime nominal. Mais elle était **sans rattrapage** :
 * un seul passage manqué, et l'échéance glissait sous `now+2j` pour ne plus
 * jamais y rentrer. Le client était alors RÉTROGRADÉ SANS PRÉAVIS — la
 * rétrogradation, elle, teste `pro_expires_at < now()` et n'oublie personne.
 *
 * L'avertissement était donc la moitié FRAGILE du cycle, et c'est celle sur
 * laquelle les CGU s'engagent : « Le client est averti par courriel avant
 * l'échéance. » Une promesse contractuelle ne peut pas dépendre d'un unique
 * passage réussi.
 *
 * En partant de `now`, toute échéance à venir dans les 3 jours est candidate à
 * chaque passage : un jour manqué est rattrapé le lendemain. Le préavis peut
 * alors être plus court que 3 jours, mais il EXISTE — ce qui vaut infiniment
 * mieux que pas d'avertissement du tout.
 *
 * Ce qui empêche la répétition : le dédoublonnage borné à 7 jours du cron
 * (`hasEmailBeenSentSince`). La fenêtre fait 3 jours, la borne 7 : un profil
 * reste candidat 3 passages de suite et n'est prévenu qu'une fois, tout en
 * redevenant éligible à la période suivante, 30 jours plus tard.
 *
 * Borne basse à `now` et non avant : un profil DÉJÀ échu mais pas encore
 * rétrogradé ne doit pas recevoir d'avertissement « avant l'échéance » — il
 * sera coupé dans le même passage et reçoit la confirmation d'expiration.
 */
export function expirationWarningWindow(now: Date = new Date()): { start: string; end: string } {
  return {
    start: new Date(now.getTime()).toISOString(),
    end: new Date(now.getTime() + EXPIRY_WARNING_LEAD_DAYS * 24 * 60 * 60 * 1000).toISOString(),
  }
}

/** Repli : durée estimée d'un verbatim texte sans transcription source (mots ÷ 150). */
export function estimateMinutesFromText(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return words / 150
}

/** Arrondi d'affichage : crédits entiers pour l'interface. */
export function displayCredits(value: number): number {
  return Math.round(value)
}
